import React from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Divider,
  Button,
  Paper,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  Payment as PaymentIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { usePosCart } from '../hooks/usePosCart';
import { usePosOrder } from '../hooks/usePosOrder';
import { formatCurrency } from '../utils/posCalculations';
import { CustomerSearch } from './CustomerSearch';

interface CartPanelProps {
  onCheckout?: () => void;
}

export const CartPanel: React.FC<CartPanelProps> = ({ onCheckout }) => {
  const {
    items,
    subTotal,
    totalIGV,
    totalDescuento,
    total,
    hasItems,
    saving,
    removeFromCart,
    updateQuantity,
    clearCart,
  } = usePosCart();
  const { openPayment } = usePosOrder();

  const handleQuantityChange = async (item: any, delta: number) => {
    const newQty = item.cantidad + delta;
    if (newQty <= 0) {
      await removeFromCart(item.idOrdenPosDetalle);
      return;
    }
    await updateQuantity(item.idOrdenPosDetalle, {
      idArticulo: item.idArticulo,
      descripcion: item.descripcion,
      unidadMedida: item.unidadMedida,
      cantidad: newQty,
      precioUnitario: item.precioUnitario,
    });
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header */}
      <Box sx={{ p: 1.5, borderBottom: 1, borderColor: 'divider' }}>
        <Typography variant="subtitle1" fontWeight="bold">
          Carrito ({items.length} items)
        </Typography>
        <CustomerSearch />
      </Box>

      {/* Items */}
      <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
        {hasItems ? (
          <List dense disablePadding>
            {items.map((item) => (
              <React.Fragment key={item.idOrdenPosDetalle}>
                <ListItem
                  sx={{ py: 0.5 }}
                  secondaryAction={
                    <IconButton
                      edge="end"
                      size="small"
                      color="error"
                      onClick={() => removeFromCart(item.idOrdenPosDetalle)}
                      disabled={saving}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  }
                >
                  <ListItemText
                    primary={
                      <Typography variant="body2" noWrap>
                        {item.descripcion}
                      </Typography>
                    }
                    secondary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                        <IconButton
                          size="small"
                          onClick={() => handleQuantityChange(item, -1)}
                          disabled={saving}
                          sx={{ p: 0.25 }}
                        >
                          <RemoveIcon fontSize="small" />
                        </IconButton>
                        <Typography variant="body2" sx={{ minWidth: 24, textAlign: 'center' }}>
                          {item.cantidad}
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={() => handleQuantityChange(item, 1)}
                          disabled={saving}
                          sx={{ p: 0.25 }}
                        >
                          <AddIcon fontSize="small" />
                        </IconButton>
                        <Typography variant="body2" color="text.secondary" sx={{ ml: 0.5 }}>
                          x {formatCurrency(item.precioUnitario)}
                        </Typography>
                        <Typography variant="body2" fontWeight="bold" sx={{ ml: 'auto' }}>
                          {formatCurrency(item.total)}
                        </Typography>
                      </Box>
                    }
                  />
                </ListItem>
                <Divider />
              </React.Fragment>
            ))}
          </List>
        ) : (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography color="text.secondary">Carrito vacio</Typography>
          </Box>
        )}
      </Box>

      {/* Totals */}
      <Paper elevation={3} sx={{ p: 1.5 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography variant="body2" color="text.secondary">Subtotal</Typography>
          <Typography variant="body2">{formatCurrency(subTotal)}</Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography variant="body2" color="text.secondary">IGV (18%)</Typography>
          <Typography variant="body2">{formatCurrency(totalIGV)}</Typography>
        </Box>
        {totalDescuento > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="body2" color="error">Descuento</Typography>
            <Typography variant="body2" color="error">-{formatCurrency(totalDescuento)}</Typography>
          </Box>
        )}
        <Divider sx={{ my: 1 }} />
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="h6" fontWeight="bold">TOTAL</Typography>
          <Typography variant="h6" fontWeight="bold" color="primary">
            {formatCurrency(total)}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            color="error"
            fullWidth
            startIcon={<CancelIcon />}
            onClick={clearCart}
            disabled={!hasItems || saving}
            size="small"
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="primary"
            fullWidth
            startIcon={<PaymentIcon />}
            onClick={() => { openPayment(); onCheckout?.(); }}
            disabled={!hasItems || saving}
            size="large"
          >
            Cobrar
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default CartPanel;
